import { useState, useCallback } from 'react';

const ITEMS_PER_PAGE_STORAGE_KEY = 'items_per_page';

/**
 * Custom hook for managing items per page with localStorage persistence
 * Uses a single JSON object in localStorage to store all page-specific itemsPerPage values
 * 
 * localStorage structure:
 * {
 *   "projectList": 20,
 *   "verificationReportList": 15,
 *   "noticeInformationList": 10,
 *   ...
 * }
 * 
 * @param {string} pageKey - The key for this specific page (e.g., 'projectList')
 * @param {number} defaultValue - Default value if no stored value exists
 * @returns {Object} - { itemsPerPage, setItemsPerPage, handleItemsPerPageChange }
 * 
 * @example
 * // In a component:
 * const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(
 *   ITEMS_PER_PAGE_KEY.PROJECT_LIST, 
 *   DEFAULT_ITEMS_PER_PAGE
 * );
 * 
 * const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
 *   handleItemsPerPageChange(newItemsPerPage);
 *   setCurrentPage(1); // Reset to page 1 when changing number of rows
 * };
 */
const useItemsPerPage = (pageKey, defaultValue = 10) => {
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const storedData = localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        return parsedData[pageKey] || defaultValue;
      }
      return defaultValue;
    } catch (error) {
      console.warn(`Failed to load itemsPerPage from localStorage for page "${pageKey}":`, error);
      return defaultValue;
    }
  });

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    try {
      setItemsPerPage(newItemsPerPage);
      
      // Get existing data or create new object
      const existingData = localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY);
      const data = existingData ? JSON.parse(existingData) : {};
      
      // Update the specific page key
      data[pageKey] = newItemsPerPage;
      
      // Save back to localStorage
      localStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn(`Failed to save itemsPerPage to localStorage for page "${pageKey}":`, error);
      // Still update the state even if localStorage fails
      setItemsPerPage(newItemsPerPage);
    }
  }, [pageKey]);

  return {
    itemsPerPage,
    setItemsPerPage,
    handleItemsPerPageChange,
  };
};

export default useItemsPerPage;
